import type {
  Influencer,
  InfluencerDetail,
  InfluencerOption,
  InfluencerRow,
  SearchInfluencerRow,
} from '../types/influencer.types';

export function formatFollowerCount(count?: number | null): string {
  if (!count) return '0';
  if (count >= 1_000_000) return (count / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (count >= 1_000) return (count / 1_000).toFixed(0) + 'K';
  return count.toString();
}

/** Map SearchInfluencerRow (from search_influencers RPC) to Domain Influencer entity */
export function mapSearchRowToInfluencer(row: SearchInfluencerRow): Influencer {
  return {
    id: row.id,
    profilePhoto: row.profile_photo,
    handle: row.handle,
    realName: row.real_name,
    platforms: [row.platform],
    followers: {
      instagram: row.followers_instagram,
      youtube: row.followers_youtube,
    },
    engagementRate: row.engagement_rate,
    rating: row.rating,
    tags: row.tags || [],
    status: row.status,
    location: row.location,
    collaborationsCount: row.collaborations_count,
    tier: row.tier,
    gender: row.gender,
    verified: row.verified,
    _db: {
      enrichment_status: row.enrichment_status,
      followers_count: row.followers_count,
      avg_likes: row.avg_likes,
      video_view_count: row.video_view_count,
      verified: row.verified,
      phone: row.phone,
    },
  };
}

/** Map direct DB row (from influencers table) to Domain InfluencerDetail entity */
export function mapDbRowToInfluencerDetail(inf: InfluencerRow): InfluencerDetail {
  const platformName =
    inf.platform === 'youtube'
      ? 'YouTube'
      : (inf.platform?.charAt(0).toUpperCase() || '') + (inf.platform?.slice(1) || 'Instagram');

  return {
    id: inf.id,
    profilePhoto:
      inf.profile_pic_url ||
      (inf.name
        ? inf.name
            .split(' ')
            .map((w) => w[0])
            .join('')
            .toUpperCase()
        : '??'),
    handle: inf.handle ? (inf.handle.startsWith('@') ? inf.handle : '@' + inf.handle) : '',
    realName: inf.name || '',
    platforms: [platformName],
    followers: {
      instagram: inf.platform === 'instagram' ? formatFollowerCount(inf.followers_count) : null,
      youtube: inf.platform === 'youtube' ? formatFollowerCount(inf.followers_count) : null,
    },
    engagementRate: inf.engagement_rate ?? 0,
    rating: 4.5,
    tags: Array.isArray(inf.categories) ? inf.categories : (Array.isArray(inf.tags) ? inf.tags : []),
    status: inf.verified ? 'Active' : 'Invited',
    location: inf.location ?? '',
    collaborationsCount: 0,
    tier: inf.tier ?? 'Nano',
    gender: inf.gender,
    verified: inf.verified,
    email: inf.email,
    phone: inf.phone,
    bio: inf.bio,
    birthday: inf.birthday,
    languages: inf.languages,
    categories: inf.categories,
    ratePerPost: inf.rate_per_post,
    ratePerReel: inf.rate_per_reel,
    ratePerStory: inf.rate_per_story,
    agencyManaged: inf.agency_managed,
    audienceData: inf.audience_data,
    createdAt: inf.created_at,
    updatedAt: inf.updated_at,
    _db: {
      enrichment_status: inf.enrichment_status,
      followers_count: inf.followers_count,
      avg_likes: inf.avg_likes,
      video_view_count: inf.video_view_count,
      verified: inf.verified,
      phone: inf.phone,
    },
  };
}

/** Map any influencer representation to a lean InfluencerOption for cross-domain pickers */
export function mapToInfluencerOption(item: {
  id: string;
  name?: string | null;
  realName?: string | null;
  handle?: string | null;
  profile_pic_url?: string | null;
  profilePhoto?: string | null;
  platform?: string | null;
  platforms?: string[] | null;
  followers_count?: number | null;
  tier?: string | null;
}): InfluencerOption {
  return {
    id: item.id,
    name: item.name || item.realName || '',
    handle: item.handle || '',
    profilePicUrl: item.profile_pic_url || item.profilePhoto || null,
    platform: item.platform || item.platforms?.[0] || 'Instagram',
    followersCount: item.followers_count,
    tier: item.tier,
  };
}
